import { FilterByEnum, StoreEnum, LocationLevelEnum } from "@/enums";
import MonitoringApi from "@/services/Monitoring";
import {
  IAssessment,
  IHealthFacilityData,
  IMaybe,
  INotSure,
  IPhcToAssess,
} from "@/types";
import { ApiUrl } from "@/utils/ApiNames";
import { AppRequest } from "@/utils/AppRequest";
import AppLogger from "@/utils/Logger";
import { extractDate } from "@/utils/Misc";
import { create, StateCreator } from "zustand";
import { devtools } from "zustand/middleware";

export interface MonitoringState {
  isMLoading: boolean;
  SMZone: IMaybe<string>;
  SMState: IMaybe<string>;
  SMLga: IMaybe<string>;
  SMWard: IMaybe<string>;
  SMHF: IMaybe<string>;
  assessmentRecord: IMaybe<IAssessment[]>;
  fARecord: IMaybe<IAssessment[]>;
  trendARecord: IMaybe<IAssessment[]>;
  adminLevel: LocationLevelEnum;
  isDaily: FilterByEnum;
  fPhcToAssess: IMaybe<IPhcToAssess[]>;
  phcWithErrors: IMaybe<IHealthFacilityData[]>;
  fPhcWithErrors: IMaybe<IHealthFacilityData[]>;
  showDetails: INotSure;
}

export interface MonitoringActions {
  setIsMLoading: (isMLoading: boolean) => void;
  setSMZone: (SMZone: IMaybe<string>) => void;
  setSMState: (SMState: IMaybe<string>) => void;
  setSMLga: (SMLga: IMaybe<string>) => void;
  setSMWard: (SMWard: IMaybe<string>) => void;
  setSMHF: (SMHF: IMaybe<string>) => void;
  setAssessmentRecord: (assessmentRecord: IMaybe<IAssessment[]>) => void;
  getAssessmentRecord: (query?: string, showLoader?: boolean) => void;
  setFARecord: (fARecord: IMaybe<IAssessment[]>) => void;
  setTrendARecord: (fARecord: IMaybe<IAssessment[]>) => void;
  setAdminLevel: (adminLevel: LocationLevelEnum) => void;
  setIsDaily: (isDaily: FilterByEnum) => void;
  sFPhcToAssess: (fPhcToAssess?: IPhcToAssess[]) => void;
  setPhcWithErrors: (phcWithErrors: IMaybe<IHealthFacilityData[]>) => void;
  setFPhcWithErrors: (fPhcWithErrors: IMaybe<IHealthFacilityData[]>) => void;
  setShowDetails: (showDetails: INotSure) => void;
}

export const storeCreator: StateCreator<MonitoringState & MonitoringActions> = (
  set
) => ({
  isMLoading: false,
  SMZone: null,
  SMState: null,
  SMLga: null,
  SMWard: null,
  SMHF: null,
  assessmentRecord: null,
  fARecord: null,
  trendARecord: null,
  adminLevel: LocationLevelEnum.COUNTRY,
  isDaily: FilterByEnum.ALL,
  fPhcToAssess: null,
  showDetails: null,
  phcWithErrors: null,
  fPhcWithErrors: null,
  setIsMLoading: (isMLoading) => set({ isMLoading }),
  setSMZone: (SMZone) => set({ SMZone }),
  setSMState: (SMState) => set({ SMState }),
  setSMLga: (SMLga) => set({ SMLga }),
  setSMWard: (SMWard) => set({ SMWard }),
  setSMHF: (SMHF) => set({ SMHF }),
  setAssessmentRecord: (assessmentRecord) => set({ assessmentRecord }),
  getAssessmentRecord: async (query = "", showLoader = true) => {
    const { setAssessmentRecord, setIsMLoading, setPhcWithErrors } =
      useMonitoringStore.getState();
    // setFARecord(null);
    const $url = ApiUrl.kbCollect + query;

    try {
      if (showLoader) setIsMLoading(true);
      const response = await AppRequest($url);
      const $records = response?.results || [];

      // Condition 1: Group the data by assessors and dates using reduce
      const groupedByAssessorAndDate = $records.reduce(
        (
          acc: { [x: string]: IHealthFacilityData[] },
          record: IHealthFacilityData
        ) => {
          const key = `${record["group1/group_fds/dc_name"]}-${extractDate(
            record._submission_time
          )}`;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(record);
          return acc;
        },
        {} as Record<string, IHealthFacilityData[]>
      );

      // Condition 15 Group records by zone, state, lga, ward, and hf_name
      const groupedByLocation = $records.reduce(
        (
          acc: { [x: string]: IHealthFacilityData[] },
          record: IHealthFacilityData
        ) => {
          const key = `${record["group1/group_2/zone"]}-${record["group1/group_2/state"]}-${record["group1/group_2/lga"]}-${record["group1/group_2/ward"]}-${record["group1/group_2/hf_name"]}`;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(record);
          return acc;
        },
        {} as Record<string, IHealthFacilityData[]>
      );

      // Attach errors to each record
      const recordsWithErrors = $records.map((record: IHealthFacilityData) => {
        const errors = MonitoringApi.checkConditions(record);

        // Condition 1: Assessor has visited and assessed more than three (3) HFs in a day
        const key = `${record["group1/group_fds/dc_name"]}-${extractDate(
          record?._submission_time
        )}`;
        if (groupedByAssessorAndDate[key]?.length > 3) {
          errors.push(
            "Assessor has visited and assessed more than three (3) HFs in a day"
          );
        }

        // Check if the PHC appears more than once in the same ward, lga, and state
        const locationKey = `${record["group1/group_2/zone"]}-${record["group1/group_2/state"]}-${record["group1/group_2/lga"]}-${record["group1/group_2/ward"]}-${record["group1/group_2/hf_name"]}`;

        if (
          groupedByLocation[locationKey]?.length > 1 &&
          record["group1/group_2/hf_name"]?.toLocaleLowerCase() !==
            "facilitynotseen"
        ) {
          errors.push(
            "PHC appears more than once in the same ward, LGA, and state"
          );
        }

        return {
          ...record,
          errors,
        };
      });

      // count type of errors, error_type and error_count
      const errorType: { [key: string]: number } = {};
      recordsWithErrors?.forEach((record: IHealthFacilityData) => {
        record?.errors?.forEach((error: string) => {
          if (!errorType[error]) {
            errorType[error] = 0;
          }
          errorType[error] += 1;
        });
      });
      AppLogger.info(recordsWithErrors?.length, "errorType", errorType);

      const _phcWithErrors = recordsWithErrors?.filter(
        (record: IHealthFacilityData) => record?.errors?.length
      );

      setAssessmentRecord(recordsWithErrors || []);
      setPhcWithErrors(_phcWithErrors || []);
      return response?.data;
    } catch (error) {
      return Promise.reject(error);
    } finally {
      setIsMLoading(false);
    }
  },
  setFARecord: (fARecord) => set({ fARecord }),
  setAdminLevel: (adminLevel) => set({ adminLevel }),
  setIsDaily: (isDaily) => set({ isDaily }),
  sFPhcToAssess: (fPhcToAssess) => set({ fPhcToAssess }),
  setTrendARecord: (trendARecord) => set({ trendARecord }),
  setPhcWithErrors: (phcWithErrors) => set({ phcWithErrors }),
  setShowDetails: (showDetails) => set({ showDetails }),
  setFPhcWithErrors: (fPhcWithErrors) => set({ fPhcWithErrors }),
});

const useMonitoringStore = create<MonitoringState & MonitoringActions>(
  devtools(storeCreator, { name: StoreEnum.MONITORING_STORE }) as StateCreator<
    MonitoringState & MonitoringActions
  >
);

export default useMonitoringStore;
